import { useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Camera, X, FileText, Paperclip } from "lucide-react";

/** Iter29 — unified attachment uploader.
 *  - Storage button accepts ANY file type (docs, PDFs, images, spreadsheets, videos, etc.)
 *  - Camera button uses `capture="environment"` so mobile browsers open the native camera immediately
 *    and attach the captured photo without an extra confirmation step.
 *  - Renders image thumbnails for images; renders a filename chip for anything else.
 *  Legacy default export name kept as `PhotoUploader` so all existing imports keep working.
 */
export default function PhotoUploader({ photoIds = [], onChange, testIdPrefix = "uploader", accept = "*/*", hideCamera = false }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({}); // id -> {content_type, original_filename}

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const newIds = [];
      const nextMeta = { ...meta };
      let dupCount = 0;
      let dupRefs = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newIds.push(r.data.id);
        nextMeta[r.data.id] = {
          content_type: r.data.content_type || f.type || "",
          original_filename: r.data.original_filename || f.name,
        };
        if (r.data.duplicate) {
          dupCount++;
          if ((r.data.references || []).length) dupRefs.push(...r.data.references);
        }
      }
      setMeta(nextMeta);
      onChange?.([...(photoIds || []), ...newIds]);
      if (dupCount > 0) {
        const contextMsg = dupRefs.slice(0, 2).map((ref) => `${ref.kind} ${ref.display_id || ""}`.trim()).filter(Boolean).join(", ");
        toast.info(
          contextMsg
            ? `Already uploaded — linked to existing (originally in: ${contextMsg})`
            : `${dupCount} file(s) already exist — linked to existing`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Attached ${newIds.length} file${newIds.length > 1 ? "s" : ""}`);
      }
    } catch (e) {
      toast.error("Upload failed");
    } finally { setBusy(false); }
  };

  const isImage = (id) => {
    const m = meta[id];
    if (m?.content_type?.startsWith("image/")) return true;
    if (/\.(png|jpe?g|gif|webp|heic|heif|bmp)$/i.test(m?.original_filename || "")) return true;
    // If we have no meta yet (loaded from server), optimistically try image
    return !m;
  };

  return (
    <div>
      <div className="flex gap-2 flex-wrap">
        <button type="button" data-testid={`${testIdPrefix}-pick-file`} onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 border border-[#1a1a1a] text-[#1a1a1a] px-3 py-2 text-xs uppercase tracking-wider font-bold hover:bg-[#1a1a1a] hover:text-white transition-colors duration-150">
          <Paperclip className="w-3.5 h-3.5" /> Attach any file
        </button>
        <button type="button" data-testid={`${testIdPrefix}-pick-camera`} onClick={() => cameraRef.current?.click()}
          className={`inline-flex items-center gap-2 border border-[#1a1a1a] text-[#1a1a1a] px-3 py-2 text-xs uppercase tracking-wider font-bold hover:bg-[#1a1a1a] hover:text-white transition-colors duration-150 ${hideCamera ? "hidden" : ""}`}
          title="Opens your device camera and attaches the photo as soon as you snap it (mobile).">
          <Camera className="w-3.5 h-3.5" /> Camera
        </button>
        {busy && <span className="text-[11px] font-mono text-[#1a1a1a]/60 self-center">Uploading…</span>}
        <input ref={fileRef} type="file" accept={accept} multiple hidden onChange={(e) => upload(Array.from(e.target.files || []))} />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => upload(Array.from(e.target.files || []))} />
      </div>
      {photoIds.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {photoIds.map((id) => {
            if (isImage(id)) {
              return (
                <div key={id} className="relative group">
                  <img src={fileUrl(id)} alt="" className="w-20 h-20 object-cover border border-[#1a1a1a]/30 rounded" />
                  <button type="button"
                    onClick={() => onChange?.(photoIds.filter((p) => p !== id))}
                    className="absolute -top-1 -right-1 bg-[#ff4500] text-white w-5 h-5 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            }
            const name = meta[id]?.original_filename || id.slice(0, 8);
            return (
              <div key={id} className="inline-flex items-center gap-1 bg-white border border-[#1a1a1a]/25 rounded-md px-2.5 py-1.5">
                <FileText className="w-3 h-3 text-[#1a1a1a]/55" />
                <a href={fileUrl(id)} target="_blank" rel="noreferrer" className="text-[11px] text-[#1a1a1a] hover:text-[#ff4500] max-w-[160px] truncate">{name}</a>
                <button type="button" onClick={() => onChange?.(photoIds.filter((p) => p !== id))}
                  className="ml-1 text-[#ff4500] hover:text-[#cc3500]"><X className="w-3 h-3" /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
